/**
 * Terminology service v1 — pack-driven relabeling of entity nouns
 * (Phase 1, workstream 1 of the config spine; see docs/crm/2026-07-08
 * assessment §7 Phase 1).
 *
 * PROBLEM: the CRM's user-visible vocabulary is senior living ("Prospects",
 * "Referrers", "Communities") hardcoded in JSX. A behavioral-health tenant
 * says Clients / Referral Partners / Facilities. Phase 0 made NAV labels
 * manifest-driven; this makes the IN-PAGE nouns follow.
 *
 * MODEL — three layers, resolved top-down (later wins):
 *   1. PLATFORM_TERMS      — the senior-living-flavored defaults below
 *                            (they double as the fallback when no pack
 *                            overrides a key, and as the no-provider
 *                            fallback in tests).
 *   2. Pack overrides      — `terminology` on the pack manifest
 *                            (e.g. behavioral_health: prospect → Client).
 *                            Applied in INSTALL ORDER; for the artificial
 *                            dual-operator-pack state (SL + BH on one
 *                            tenant) the later install wins — real
 *                            tenants run one vertical. Per-active-pack
 *                            context resolution is a later increment.
 *   3. Tenant overrides    — per-tenant DB table (skycrm_terminology,
 *                            V107) edited via Global Setup → Terminology
 *                            (GET/PUT /api/v1/skycrm/terminology), layered
 *                            on top of pack overrides with the same resolve
 *                            call. Sparse — one row per overridden key,
 *                            delete-to-inherit.
 *
 * KEY DESIGN: term keys are a CLOSED union (typo = compile error), values
 * are display-cased ("Referral Partner"). Sentence-case helpers live at
 * the call site (`.toLowerCase()` where prose needs it).
 *
 * BUNDLE NOTE: this module is imported by the CLIENT (PackContext) for
 * defaults + resolution. It must never import pack manifests — only the
 * reverse (manifests declare overrides; the server ships them over the
 * wire).
 */

export type TermKey =
  | "prospect.singular"
  | "prospect.plural"
  | "referrer.singular"
  | "referrer.plural"
  | "resident.singular"
  | "resident.plural"
  | "organization.singular"
  | "organization.plural"
  | "community.singular"
  | "community.plural"
  // E2 Discovery surface (STATUS-mi.md D15) — relabelable per vertical/
  // tenant (e.g. BH: "Engagement drivers" for motivators).
  | "discovery.singular"
  | "motivator.singular"
  | "motivator.plural"
  | "barrier.singular"
  | "barrier.plural"
  // D1 commitments (STATUS-advances.md D1). The word in the CODE is
  // `commitment`, deliberately and permanently: "advance" is live CLINICAL
  // vocabulary in senior living (skycrm_care_plans.advance_directive_on_file
  // ships today), so naming a sales object after it invites a reader to
  // conflate a sales commitment with an advance directive.
  //
  // Touchmark asked for "advancement". THESE TWO KEYS ARE HOW THEY GET IT —
  // the terminology layer relabels the surface without the codebase adopting a
  // word that already means two other things. Same move `discovery.singular`
  // made for the coach panel title.
  | "commitment.singular"
  | "commitment.plural";

export type Terminology = Record<TermKey, string>;
export type TerminologyOverrides = Partial<Record<TermKey, string>>;

/** Platform defaults — today's senior-living-flavored vocabulary. */
export const PLATFORM_TERMS: Terminology = {
  "prospect.singular": "Prospect",
  "prospect.plural": "Prospects",
  "referrer.singular": "Referrer",
  "referrer.plural": "Referrers",
  "resident.singular": "Resident",
  "resident.plural": "Residents",
  "organization.singular": "Organization",
  "organization.plural": "Organizations",
  "community.singular": "Community",
  "community.plural": "Communities",
  "discovery.singular": "Discovery",
  "motivator.singular": "Motivator",
  "motivator.plural": "Motivators",
  "barrier.singular": "Barrier",
  "barrier.plural": "Barriers",
  "commitment.singular": "Commitment",
  "commitment.plural": "Commitments",
};

/** Human-readable descriptions for the settings editor. */
export const TERM_DESCRIPTIONS: Record<TermKey, string> = {
  "prospect.singular": "A person in the sales funnel (before conversion)",
  "prospect.plural": "The sales-funnel list and related surfaces",
  "referrer.singular": "A professional referral source",
  "referrer.plural": "The referral-sources list and related surfaces",
  "resident.singular": "A person after move-in / admission",
  "resident.plural": "The post-conversion roster",
  "organization.singular": "A partner organization",
  "organization.plural": "The organizations directory",
  "community.singular": "A location / property / facility",
  "community.plural": "The locations directory",
  "discovery.singular":
    "The structured discovery surface on a prospect (motivators, barriers, readiness)",
  "motivator.singular": "A force driving the person toward change / moving",
  "motivator.plural": "The motivator list on the Discovery surface",
  "barrier.singular": "A force holding the person back from change / moving",
  "barrier.plural": "The barrier list on the Discovery surface",
  "commitment.singular":
    "Something the prospect agreed to do next (some teams call it an advancement)",
  "commitment.plural": "The list of what prospects have agreed to do next",
};

/** Runtime list of valid keys — the API layer validates writes against this. */
export const TERM_KEYS = Object.keys(PLATFORM_TERMS) as TermKey[];

export function isTermKey(v: string): v is TermKey {
  return (TERM_KEYS as string[]).includes(v);
}

/**
 * Resolve the effective terminology from override layers, applied in
 * order (later wins). Callers pass pack overrides in install order,
 * then the tenant's DB overrides as the final layer.
 */
export function resolveTerminology(overrideLayers: TerminologyOverrides[]): Terminology {
  const resolved: Terminology = { ...PLATFORM_TERMS };
  for (const layer of overrideLayers) {
    for (const [key, value] of Object.entries(layer) as [TermKey, string][]) {
      if (value) resolved[key] = value;
    }
  }
  return resolved;
}
